import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FiUsers, FiFolder, FiCalendar, FiFileText, FiLogOut, FiMessageSquare } from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import Breadcrumbs from "../components/common/Breadcrumbs";
import { AuthContext } from "../context/AuthContext";
import { CommunityContext } from "../context/CommunityContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { Button, Card, Tabs, EmptyState, Spinner, Modal, Textarea, Badge } from "../components/ui";
import { getCommunity } from "../api/communities";
import { createJoinRequest, getUserJoinRequests } from "../api/joinRequests";
import uploadUrl from "../utils/assetUrl";
import OverviewTab from "../components/community/OverviewTab";
import MembersTab from "../components/community/MembersTab";
import ChatTab from "../components/community/ChatTab";
import ProjectsTab from "../components/community/ProjectsTab";
import EventsTab from "../components/community/EventsTab";

const TAB_LABELS = {
  overview: "Overview",
  members: "Members",
  chat: "Chat",
  projects: "Projects",
  events: "Events",
};

export default function CommunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin } = useContext(AuthContext);
  const { joinedCommunities, leave } = useContext(CommunityContext);
  const { notify } = useToast();
  const confirm = useConfirm();

  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [actionBusy, setActionBusy] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [requestModal, setRequestModal] = useState(false);
  const [requestMsg, setRequestMsg] = useState("");

  const isMember = joinedCommunities.some((c) => String(c.id) === String(id));
  // Admins have full access to every community without needing to join —
  // treat them as "joined" for gating purposes.
  const isJoined = isMember || isAdmin;
  const canAccessFullCommunity = isJoined;

  useEffect(() => {
    setLoading(true);
    getCommunity(id)
      .then((res) => setCommunity(res.data?.community || res.data))
      .catch(() => notify("Couldn't load community.", "error"))
      .finally(() => setLoading(false));
    if (user?.id) {
      getUserJoinRequests(user.id)
        .then((res) => {
          const pending = (res.data?.requests || []).find((r) => String(r.communityId) === String(id) && r.status === "Pending");
          setHasPendingRequest(Boolean(pending));
        })
        .catch(() => {});
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guard against landing on a locked tab (e.g. via a stale ?tab= link)
  // once membership/admin status is known.
  useEffect(() => {
    if (community && !isJoined && !["overview", "members"].includes(tab)) {
      setTab("overview");
    }
  }, [community, isJoined, tab]);

  const handleRequestJoin = async () => {
    setActionBusy(true);
    try {
      await createJoinRequest(Number(id), requestMsg);
      notify("Join request sent! An admin will review it.", "success");
      setHasPendingRequest(true);
      setRequestModal(false);
      setRequestMsg("");
    } catch (e) {
      notify(e.response?.data?.message || "Couldn't send request.", "error");
    } finally {
      setActionBusy(false);
    }
  };

  const handleLeave = async () => {
    if (!(await confirm({ title: "Leave this community?", description: "You will need to send a new join request to rejoin later." }))) return;
    setActionBusy(true);
    try {
      await leave(id);
      notify("You've left the community.", "success");
    } catch {
      notify("Couldn't leave.", "error");
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <AppShell title="Community" wide>
        <div className="flex justify-center py-24">
          <Spinner className="h-8 w-8" />
        </div>
      </AppShell>
    );
  }
  if (!community) {
    return (
      <AppShell title="Community" wide>
        <EmptyState icon={<FiUsers className="h-6 w-6" />} title="Community not found" action={<Button onClick={() => navigate("/communities")}>Back</Button>} />
      </AppShell>
    );
  }

  // Non-members only get Overview + Members — the rest unlocks after joining.
  const tabs = canAccessFullCommunity
    ? [
        { value: "overview", label: "Overview", icon: <FiFileText className="h-4 w-4" /> },
        { value: "members", label: "Members", icon: <FiUsers className="h-4 w-4" />, count: community.memberCount ?? undefined },
        { value: "chat", label: "Chat", icon: <FiMessageSquare className="h-4 w-4" /> },
        { value: "projects", label: "Projects", icon: <FiFolder className="h-4 w-4" /> },
        { value: "events", label: "Events", icon: <FiCalendar className="h-4 w-4" /> },
      ]
    : [
        { value: "overview", label: "Overview", icon: <FiFileText className="h-4 w-4" /> },
        { value: "members", label: "Members", icon: <FiUsers className="h-4 w-4" />, count: community.memberCount ?? undefined },
      ];

  return (
    <AppShell title={community.name} subtitle="Community" wide>
      <Breadcrumbs
        items={[
          { label: "Communities", to: "/communities" },
          { label: community.name, to: `/communities/${id}` },
          { label: TAB_LABELS[tab] || "Overview" },
        ]}
      />

      <Card className="overflow-hidden">
        <div className="relative h-40 bg-gradient-to-r from-primary-600 to-primary-800 sm:h-48">
          {community.image && <img src={uploadUrl(community.image)} alt="" className="h-full w-full object-cover" />}
        </div>
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{community.name}</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">{community.description}</p>
            <div className="mt-2 flex items-center gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <FiUsers className="h-3.5 w-3.5" /> {community.memberCount ?? 0} members
              </span>
            </div>
          </div>
          {isMember ? (
            <Button variant="outlineDanger" onClick={handleLeave} loading={actionBusy}>
              <FiLogOut className="h-4 w-4" /> Leave
            </Button>
          ) : isAdmin ? (
            <Badge variant="primary">Admin access</Badge>
          ) : hasPendingRequest ? (
            <Button variant="secondary" disabled>Request pending…</Button>
          ) : (
            <Button onClick={() => setRequestModal(true)} loading={actionBusy}>Request to join</Button>
          )}
        </div>
        <Tabs tabs={tabs} active={tab} onChange={setTab} className="px-5 sm:px-6" />
      </Card>

      <div className="mt-6">
        {tab === "overview" && <OverviewTab community={community} onNavigateToTab={setTab} />}
        {tab === "members" && <MembersTab communityId={id} isAdmin={isAdmin} currentUserId={user?.id} />}
        {tab === "chat" && <ChatTab communityId={id} currentUser={user} isJoined={isJoined} />}
        {tab === "projects" && <ProjectsTab communityId={id} isAdmin={isAdmin} currentUserId={user?.id} />}
        {tab === "events" && <EventsTab communityId={id} isAdmin={isAdmin} />}
      </div>

      {/* Join request modal */}
      <Modal
        open={requestModal}
        onClose={() => setRequestModal(false)}
        title="Request to join"
        description={`Send a request to join ${community.name}.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRequestModal(false)}>Cancel</Button>
            <Button onClick={handleRequestJoin} loading={actionBusy}>Send request</Button>
          </>
        }
      >
        <Textarea value={requestMsg} onChange={(e) => setRequestMsg(e.target.value)} label="Message (optional)" placeholder="Tell the admin why you'd like to join…" rows={3} />
      </Modal>
    </AppShell>
  );
}
